import { z } from 'zod';

const PeriodSchema = z.object({
    from: z.string(),
    to: z.string(),
    reason: z.string(),
    demo_id: z.number().optional().nullable(),
});

const AvailabilitySchema = z.object({
    isAvailable: z.boolean().default(true),
    unavailablePeriods: z.array(PeriodSchema),
    cleaningFee: z.coerce.number(),
});

const GameRoomSchema = z
    .object({
        spaceType: z.literal('gameRoom'),
        gameConsole: z.boolean().default(false),
        poolTable: z.boolean().default(false),
        soundSystem: z.boolean().default(false),
        tv: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const GarageSchema = z
    .object({
        spaceType: z.literal('garage'),
        parkingCapacity: z.number().default(0),
        evChargingStation: z.boolean().default(false),
        securitySystem: z.boolean().default(false),
        automaticDoor: z.boolean().default(false),
        dehumidifier: z.boolean().default(false),
        carwashStation: z.boolean().default(false),
        bicycleRack: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const BedroomSchema = z
    .object({
        assignedName: z.string().min(1, { message: 'Required' }),
        spaceType: z.literal('bedroom'),
        blackOutCurtains: z.boolean().default(false),
        wardrobe: z.boolean().default(false),
        extraPillowsAndBlankets: z.boolean().default(false),
        iron: z.boolean().default(false),
        safe: z.boolean().default(false),
        tv: z.boolean().default(false),
        ensuite: z.boolean().default(false),
        monitor: z.boolean().default(false),
        singleBed: z.number().default(0),
        doubleBed: z.number().default(0),
        queenBed: z.number().default(0),
        kingBed: z.number().default(0),
        bunkBed: z.number().default(0),
        sofaBed: z.number().default(0),
        couch: z.number().default(0),
        babyCot: z.number().default(0),
        desk: z.number().default(0),
        sleepingCapacity: z.number().optional().nullable(),
    })
    .merge(AvailabilitySchema);

const BathroomSchema = z
    .object({
        spaceType: z.literal('bathroom'),
        bathtub: z.boolean().default(false),
        babyBath: z.boolean().default(false),
        hairdryer: z.boolean().default(false),
        heating: z.boolean().default(false),
        bidet: z.boolean().default(false),
        toiletries: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const BoardroomSchema = z
    .object({
        spaceType: z.literal('boardroom'),
        seatingCapacity: z.number().default(0),
        projector: z.boolean().default(false),
        whiteboard: z.boolean().default(false),
        soundSystem: z.boolean().default(false),
        tv: z.boolean().default(false),
        outletsAndChargers: z.boolean().default(false),
        transmittingEquipment: z.boolean().default(false),
        camera: z.boolean().default(false),
        zoom: z.boolean().default(false),
        msTeams: z.boolean().default(false),
        googleMeet: z.boolean().default(false),
        microphone: z.boolean().default(false),
        printingAndScanning: z.boolean().default(false),
        cableAdapters: z.boolean().default(false),
        description: z.string().default(''),
        assignedName: z.string().default(''),
        id: z.number().optional(),
    })
    .merge(AvailabilitySchema);

const DiningAreaSchema = z
    .object({
        spaceType: z.literal('diningArea'),
        seatingCapacity: z.number().default(0),
        tv: z.boolean().default(false),
        soundSystem: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const KitchenSchema = z
    .object({
        spaceType: z.literal('kitchen'),
        fridge: z.boolean().default(false),
        oven: z.boolean().default(false),
        microwave: z.boolean().default(false),
        dishwasher: z.boolean().default(false),
        coffeeMachine: z.boolean().default(false),
        toaster: z.boolean().default(false),
        kettle: z.boolean().default(false),
        cookware: z.boolean().default(false),
        cutlery: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const LivingRoomSchema = z
    .object({
        spaceType: z.literal('livingRoom'),
        singleBed: z.number().default(0),
        doubleBed: z.number().default(0),
        queenBed: z.number().default(0),
        kingBed: z.number().default(0),
        bunkBed: z.number().default(0),
        sofaBed: z.number().default(0),
        couch: z.number().default(0),
        babyCot: z.number().default(0),
        wardrobe: z.boolean().default(false),
        extraPillowsAndBlankets: z.boolean().default(false),
        iron: z.boolean().default(false),
        safe: z.boolean().default(false),
        tv: z.boolean().default(false),
        fireplace: z.boolean().default(false),
        poolTable: z.boolean().default(false),
    })
    .merge(AvailabilitySchema);

const PrivateOfficeSchema = z
    .object({
        spaceType: z.literal('privateOffice'),
        assignedName: z.string().min(1, { message: 'Required' }),
        description: z.string().default(''),
        outletsAndChargers: z.boolean().default(false),
        tv: z.boolean().default(false),
        transmittingEquipment: z.boolean().default(false),
        camera: z.boolean().default(false),
        zoom: z.boolean().default(false),
        msTeams: z.boolean().default(false),
        googleMeet: z.boolean().default(false),
        soundSystem: z.boolean().default(false),
        microphone: z.boolean().default(false),
        whiteboard: z.boolean().default(false),
        printingAndScanning: z.boolean().default(false),
        monitor: z.boolean().default(false),
        keyboard: z.boolean().default(false),
        mouse: z.boolean().default(false),
        dockingStation: z.boolean().default(false),
        ergonomicChair: z.boolean().default(false),
        adjustabledDesk: z.boolean().default(false),
        bed: z.boolean().default(false),
        dedicatedDesk: z.boolean().default(false),
        closet: z.boolean().default(false),
        bathroom: z.boolean().default(false),
        id: z.number().optional(),
    })
    .merge(AvailabilitySchema);

export const SpaceSchema = z.discriminatedUnion('spaceType', [
    GameRoomSchema,
    GarageSchema,
    BedroomSchema,
    BathroomSchema,
    BoardroomSchema,
    DiningAreaSchema,
    KitchenSchema,
    LivingRoomSchema,
    PrivateOfficeSchema,
]);
